import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, ArrowDown, ArrowUp, X, Clock3 } from 'lucide-react';
import { api, type Job } from '../api.js';
import { openStream } from '../stream.js';
import { Screen, Section, Card, Badge, EmptyState, ago, minutes, Sha } from '../ui/kit.js';
import { useIsDesktop } from '../ui/useIsDesktop.js';
import { meta, lifeMeta, RAIL_LABELS, WORKSHOP_RAIL_LABELS, PAGES_RAIL_LABELS, ATTENTION, MOVING, WAITING, LIFECYCLE_ATTENTION, LIFECYCLE_MOVING } from '../phases.js';
import { JobDetail } from './JobDetail.js';
import { reporter } from '../pwa/report.js';

/* The line's name without the family prefix: "Opus 5.5", "Fable 5.1". */
const modelWord = (j: Job) => (j.modelLabel ?? j.model).replace(/^Claude /, '');

// The live board: what waits on Niklas, what is in the works, the queue in
// its true order (reorderable), and a bounded page of what landed. On a
// desk the board sits beside the open job; j/k walk it, Enter opens.

const PAGE = 15;

export function JobsSplit() {
  const { id } = useParams();
  const desktop = useIsDesktop();
  if (!desktop) return id ? <JobDetail /> : <JobsList />;
  return (
    <div className="ops-split">
      <div className="ops-split__list">
        <JobsList selectedId={id} embedded />
      </div>
      <div className="ops-split__detail">
        {id ? (
          <JobDetail key={id} />
        ) : (
          <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            <EmptyState icon={<Activity />} title="Pick a job">
              Its log, CI, screenshots and cost open here.
            </EmptyState>
          </div>
        )}
      </div>
    </div>
  );
}

export function JobsList(props: { selectedId?: string; embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const [active, setActive] = useState<Job[] | null>(null);
  const [landed, setLanded] = useState<Job[]>([]);
  const [more, setMore] = useState(false);
  const cursor = useRef<string | null>(null);
  const [cursorTick, setCursorTick] = useState(0);

  const loadActive = useCallback(
    () => api.get<{ jobs: Job[] }>('/api/jobs?scope=active').then((r) => setActive(r.jobs)).catch(reporter('the job list')),
    [],
  );
  const loadLandedFirst = useCallback(
    () =>
      api
        .get<{ jobs: Job[]; more: boolean; cursor: string | null }>(`/api/jobs?scope=landed&limit=${PAGE}`)
        .then((r) => {
          setLanded(r.jobs);
          setMore(r.more);
          cursor.current = r.cursor;
        })
        .catch(reporter('the job list')),
    [],
  );
  const loadMore = () => {
    if (!cursor.current) return;
    void api
      .get<{ jobs: Job[]; more: boolean; cursor: string | null }>(
        `/api/jobs?scope=landed&limit=${PAGE}&before=${encodeURIComponent(cursor.current)}`,
      )
      .then((r) => {
        setLanded((l) => [...l, ...r.jobs]);
        setMore(r.more);
        cursor.current = r.cursor;
        setCursorTick((t) => t + 1);
      });
  };

  useEffect(() => {
    void loadActive();
    void loadLandedFirst();
    const stream = openStream('/api/events', {
      job: () => {
        void loadActive();
        void loadLandedFirst();
      },
    });
    return () => stream.close();
  }, [loadActive, loadLandedFirst]);

  // j/k walk the visible board on a keyboard; Enter opens.
  const flat = [
    ...(active ?? []).filter((j) => (j.lifecycle ? LIFECYCLE_ATTENTION.includes(j.lifecycle) : ATTENTION.includes(j.phase))),
    ...(active ?? []).filter((j) => (j.lifecycle ? LIFECYCLE_MOVING.includes(j.lifecycle) : MOVING.includes(j.phase))),
    ...(active ?? []).filter((j) => WAITING.includes(j.phase)),
    ...landed,
  ];
  const [cursorIdx, setCursorIdx] = useState(-1);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'j' || e.key === 'k') {
        e.preventDefault();
        setCursorIdx((i) => Math.max(0, Math.min(flat.length - 1, i + (e.key === 'j' ? 1 : -1))));
      } else if (e.key === 'Enter' && cursorIdx >= 0 && flat[cursorIdx]) {
        e.preventDefault();
        navigate(`/jobs/${flat[cursorIdx].id}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flat, cursorIdx, navigate]);

  if (!active) {
    return (
      <Screen title="Jobs">
        <EmptyState title="Reading the board…" />
      </Screen>
    );
  }

  /* Grouped by what the JOB needs, not by what its session is doing: a job
     the Finisher is driving is not waiting on anybody. */
  const needs = (j: Job) => (j.lifecycle ? LIFECYCLE_ATTENTION.includes(j.lifecycle) : ATTENTION.includes(j.phase));
  const going = (j: Job) => (j.lifecycle ? LIFECYCLE_MOVING.includes(j.lifecycle) : MOVING.includes(j.phase));
  const attention = active.filter(needs);
  const moving = active.filter((j) => !needs(j) && going(j));
  const waiting = active.filter((j) => !needs(j) && !going(j));
  const open = (j: Job) => navigate(j.buttons?.includes('ship') && j.phase === 'green_held' ? `/review/${j.id}` : `/jobs/${j.id}`);
  const mark = (j: Job) => (flat[cursorIdx]?.id === j.id ? ' ops-card--cursor' : '') + (props.selectedId === j.id ? ' ops-card--selected' : '');

  return (
    <Screen title="Jobs" sub={moving.length ? `${moving.length} in the works` : 'Nothing running'}>
      {attention.length > 0 ? (
        <Section title="Waiting on you">
          {attention.map((j) => (
            <JobCard key={j.id} job={j} onTap={() => open(j)} className={mark(j)} />
          ))}
        </Section>
      ) : null}
      <Section title="In the works">
        {moving.length === 0 ? (
          <EmptyState icon={<Activity />} title="The box is idle.">
            Compose a change and it starts within seconds.
          </EmptyState>
        ) : (
          moving.map((j) => <JobCard key={j.id} job={j} onTap={() => navigate(`/jobs/${j.id}`)} className={mark(j)} />)
        )}
      </Section>
      {waiting.length > 0 ? (
        <Section title="Queued" aside={waiting.length > 1 ? 'runs in this order' : undefined}>
          <QueueList jobs={waiting} onOpen={(j) => navigate(`/jobs/${j.id}`)} onChanged={loadActive} />
        </Section>
      ) : null}
      {landed.length > 0 ? (
        <Section title="Landed">
          {landed.map((j) => (
            <JobCard key={j.id} job={j} onTap={() => navigate(`/jobs/${j.id}`)} compact className={mark(j)} />
          ))}
          {more ? (
            <button type="button" className="ops-more" onClick={loadMore} data-testid="jobs-more">
              Show more
            </button>
          ) : null}
        </Section>
      ) : null}
    </Screen>
  );
}

// The queue in its true order: arrows to reorder, X to cancel.
function QueueList(props: { jobs: Job[]; onOpen: (j: Job) => void; onChanged: () => void }) {
  // The true run order: the server's queue position, arrival otherwise.
  const queued = props.jobs
    .filter((j) => j.phase === 'queued')
    .sort((a, b) => (a.queuePosition ?? 1e9) - (b.queuePosition ?? 1e9) || a.createdAt.localeCompare(b.createdAt));
  const scheduled = props.jobs.filter((j) => j.phase === 'scheduled');
  const move = (id: string, delta: number) => {
    const order = queued.map((j) => j.id);
    const idx = order.indexOf(id);
    const to = idx + delta;
    if (idx < 0 || to < 0 || to >= order.length) return;
    order.splice(idx, 1);
    order.splice(to, 0, id);
    void api.post('/api/jobs/queue/reorder', { order }).then(props.onChanged).catch(reporter('the queue order'));
  };
  const cancel = (id: string) =>
    void api.post(`/api/jobs/${id}/cancel`).then(props.onChanged).catch(reporter('the cancel'));
  return (
    <div className="ops-rows" data-testid="queue">
      {queued.map((j, i) => (
        <div key={j.id} className="ops-row">
          <button type="button" className="ops-row__main" style={{ textAlign: 'left', minHeight: 44 }} onClick={() => props.onOpen(j)}>
            <div className="ops-row__title">
              #{i + 1} {j.title}
            </div>
            <div className="ops-row__sub">
              {j.kind === 'workshop' ? 'Workshop' : j.mode === 'ship' ? 'Ship' : 'Hold'} · {modelWord(j)} · {ago(j.createdAt)}
            </div>
          </button>
          <div className="ops-row__end ops-blockrow__tools">
            <button type="button" aria-label={`Move ${j.title} up`} disabled={i === 0} onClick={() => move(j.id, -1)}>
              <ArrowUp size={15} />
            </button>
            <button type="button" aria-label={`Move ${j.title} down`} disabled={i === queued.length - 1} onClick={() => move(j.id, 1)}>
              <ArrowDown size={15} />
            </button>
            <button type="button" aria-label={`Cancel ${j.title}`} onClick={() => cancel(j.id)}>
              <X size={15} />
            </button>
          </div>
        </div>
      ))}
      {scheduled.map((j) => (
        <div key={j.id} className="ops-row">
          <button type="button" className="ops-row__main" style={{ textAlign: 'left', minHeight: 44 }} onClick={() => props.onOpen(j)}>
            <div className="ops-row__title">{j.title}</div>
            <div className="ops-row__sub">
              <Clock3 size={11} style={{ verticalAlign: '-1px' }} /> runs{' '}
              {j.scheduledFor ? new Date(j.scheduledFor).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : 'later'}
            </div>
          </button>
          <div className="ops-row__end ops-blockrow__tools">
            <button type="button" aria-label={`Cancel ${j.title}`} onClick={() => cancel(j.id)}>
              <X size={15} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function JobCard(props: { job: Job; onTap: () => void; compact?: boolean; className?: string }) {
  const j = props.job;
  /* V8 §11: the board leads with the LIFECYCLE, which is what the daemon
     drives, not with `phase`, which describes the session's process. The
     difference is the whole round: a job whose session ended read "Done"
     here while its CI was red and nothing was finished. "The session said
     it was done" is a session event and must never be shown as a
     completion. `phase` still draws the rail below, where it belongs. */
  const m = j.lifecycle ? lifeMeta(j.lifecycle) : meta(j.phase);
  return (
    <Card onTap={props.onTap} className={props.className?.trim() || undefined}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="ops-row__title" style={{ fontSize: 15 }}>
            {j.title}
          </div>
          <div className="ops-row__sub" style={{ marginTop: 3 }}>
            {j.repo && j.repo !== 'meridian' ? 'DHS demo · ' : ''}
            {j.kind === 'workshop' ? 'Workshop' : j.mode === 'ship' ? 'Ship' : 'Hold'} · {modelWord(j)}
            {j.modelCheck?.state === 'mismatch' ? (
              <b className="ops-jobwarn" data-testid="job-wrong-model">
                {' '}(wrong model)
              </b>
            ) : null}
            {j.effort ? ` · ${j.effort}` : ''}
            {j.prNumber ? <> · PR #{j.prNumber}</> : null}
            {j.costUsd ? ` · $${j.costUsd.toFixed(2)}` : ''}
            {' · '}
            {j.finishedAt ? `took ${minutes(j.startedAt, j.finishedAt)}` : j.startedAt ? minutes(j.startedAt) : ago(j.createdAt)}
          </div>
        </div>
        <Badge tone={m.tone} pulse={m.live}>
          {m.label}
          {j.phase === 'queued' && j.queuePosition ? ` #${j.queuePosition}` : ''}
        </Badge>
      </div>
      {!props.compact ? <Rail phase={j.phase} kind={j.kind} journey={j.repo && j.repo !== 'meridian' ? 'pages' : 'staging'} /> : null}
      {j.question ? (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ops-accent)' }}>{j.question.text}</p>
      ) : null}
      {j.state === 'ended' && j.ending && j.ending.kind !== 'done' ? (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ops-bad)' }}>{j.ending.detail}</p>
      ) : null}
      {j.state === 'running' && j.facts?.openTool ? (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ops-ink-subtle)' }}>
          {j.facts.openTool.detail ?? j.facts.openTool.name} · running {minutes(j.facts.openTool.since)}
        </p>
      ) : null}
      {j.phase === 'staging_verified' && j.mergeCommitSha ? (
        <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--ops-ok)' }}>
          Verified on staging as <Sha value={j.mergeCommitSha} />. Promotable.
        </p>
      ) : null}
    </Card>
  );
}

export function Rail(props: {
  phase: string;
  kind?: 'change' | 'workshop';
  journey?: 'staging' | 'pages';
  /** Where the facts say the work got to. The phase still chooses the
      colour; it no longer chooses the position (V9). */
  reached?: number;
}) {
  const m = meta(props.phase);
  const bad = ['red', 'crashed', 'auth', 'api_error', 'limit'].includes(props.phase);
  const labels =
    props.kind === 'workshop' ? WORKSHOP_RAIL_LABELS : props.journey === 'pages' ? PAGES_RAIL_LABELS : RAIL_LABELS;
  const at = props.reached ?? m.railIndex;
  const idx = Math.min(at, labels.length - 1);
  return (
    <div className="ops-rail" style={{ marginTop: 12 }}>
      {labels.map((label, i) => (
        <span key={label} style={{ display: 'contents' }}>
          {i > 0 ? <span className="ops-rail__link" /> : null}
          <span
            className={`ops-rail__step${
              i < idx ? ' ops-rail__step--done' : i === idx ? ` ops-rail__step--now${bad ? ' ops-rail__step--bad' : ''}` : ''
            }`}
          >
            <span className="ops-rail__dot" />
            {label}
          </span>
        </span>
      ))}
    </div>
  );
}
